import { type Component, Index, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import {
  ANTI_FLASH_GATE_MS,
  useSearchRequestClock,
} from "../hooks/useSearchRequestClock";

const SKELETON_STAGGER_MS = 60;

type StepState = "past" | "active" | "future";

const stepState = (index: number, activeIndex: number): StepState =>
  index === activeIndex ? "active" : index < activeIndex ? "past" : "future";

export type SearchLoadingStepsProps = {
  /**
   * Istante di partenza della richiesta di prima ricerca (posseduto dalla
   * route, mai dal mount): la narrazione riprende dal punto giusto dopo un
   * giro di tab (AC2/E-3) e si ri-ancora quando cambia (retry post-errore).
   */
  startedAt: number;
};

/**
 * La loading screen a step della prima ricerca attiva — variante «Cantiere»
 * della run `/prototype` portata in produzione (SPEC AC1–AC6): pill di stato
 * AI (anello in rotazione attorno alla sparkle) al posto del conteggio,
 * messaggi in crossfade impilati in grid, tre sagome shimmer dei risultati.
 *
 * Gate anti-flash: non renderizza nulla finché la richiesta non è in volo da
 * almeno `ANTI_FLASH_GATE_MS`, valutato contro `startedAt` — una risposta
 * rapida non produce mai un lampo (AC3), un remount a richiesta vecchia
 * mostra la schermata subito (D13).
 *
 * Screen reader: una sola announce statica (`role="status"`); gli step visivi
 * sono `aria-hidden` per non annunciare quattro volte in venti secondi (E-24).
 */
export const SearchLoadingSteps: Component<SearchLoadingStepsProps> = (
  props,
) => {
  const { t } = useI18n();
  const clock = useSearchRequestClock(() => props.startedAt);
  const prefersReduced = usePrefersReducedMotion();
  const visible = () => clock.elapsed() >= ANTI_FLASH_GATE_MS;

  const stepLabels = () => [
    t("company.search.freelancers.result.loading_steps.step_1"),
    t("company.search.freelancers.result.loading_steps.step_2"),
    t("company.search.freelancers.result.loading_steps.step_3"),
    t("company.search.freelancers.result.loading_steps.step_4"),
  ];

  return (
    <Show when={visible()}>
      <div class="space-y-4">
        <p class="sr-only" role="status">
          {t("company.search.freelancers.result.loading_steps.sr_status")}
        </p>

        {/* La pill di stato AI: al posto del conteggio risultati. */}
        <div aria-hidden="true" class="flex flex-wrap items-center gap-3">
          {/* `min-w-0`: la pill deve poter scendere sotto il proprio
              max-content, altrimenti su viewport stretti (o dentro un
              contenitore già compresso) sfonda il bordo destro invece di
              mandare a capo il messaggio. */}
          <div class="inline-flex min-w-0 items-center gap-2.5 rounded-full border border-primary/25 bg-card px-4 py-2 shadow-sm">
            {/* L'anello che gira attorno alla sparkle: segnale di caricamento
                e identità AI in un solo glifo. Sotto reduced motion resta la
                sola sparkle statica. */}
            <span class="relative flex h-5 w-5 shrink-0 items-center justify-center">
              <Show
                when={!prefersReduced()}
                fallback={
                  <i class="ph-fill ph-sparkle text-base text-primary" />
                }
              >
                <i class="ph ph-circle-notch absolute inset-0 flex animate-spin items-center justify-center text-xl text-primary" />
                <i class="ph-fill ph-sparkle text-[10px] text-primary" />
              </Show>
            </span>
            {/* Traccia unica `minmax(0,max-content)`: su viewport larghi la
                griglia è larga quanto il messaggio più lungo dei quattro (mai
                quanto quello attivo, quindi nessun reflow al cambio di step);
                su viewport stretti può scendere sotto il max-content e i
                messaggi vanno a capo invece di sfondare la pill. */}
            <span class="grid min-w-0 grid-cols-[minmax(0,max-content)] text-sm text-foreground">
              <Index each={stepLabels()}>
                {(label, index) => (
                  <span
                    data-state={stepState(index, clock.stepIndex())}
                    class="search-step-msg"
                  >
                    {label()}
                  </span>
                )}
              </Index>
            </span>
          </div>
          <Show when={clock.isLongWait()}>
            {/* `gray-600` e non `text-muted-foreground`: quel token misura
                4.40:1 sullo sfondo pagina, sotto la soglia AA di 4.5:1 (WCAG
                1.4.3) — e questa riga è testo normale a 12px, quindi la soglia
                piena si applica. gray-600 sta a 6.99:1. */}
            <p class="search-long-wait text-xs text-gray-600">
              {t("company.search.freelancers.result.loading_steps.long_wait")}
            </p>
          </Show>
        </div>

        {/* Le sagome dei risultati che stanno arrivando. */}
        <div aria-hidden="true" class="space-y-4">
          <Index each={[0, 1, 2]}>
            {(_, index) => (
              <div
                class="search-rise rounded-lg border border-border bg-card p-6"
                style={{
                  "animation-delay": `${index * SKELETON_STAGGER_MS}ms`,
                }}
              >
                {/* Ogni sagoma porta `max-w-full`: le larghezze fisse sono la
                    forma ideale della card, non un minimo: su viewport stretti
                    devono cedere invece di uscire dal bordo. Sotto reduced
                    motion queste barre sono l'unico segnale di caricamento
                    rimasto (l'anello sparisce, AC6), quindi non possono
                    rompersi. */}
                <div class="flex items-start gap-4">
                  <div class="skeleton-shimmer h-16 w-16 max-w-full shrink-0 rounded-full" />
                  <div class="min-w-0 flex-1 space-y-2">
                    <div class="skeleton-shimmer h-4 w-44 max-w-full rounded" />
                    <div class="skeleton-shimmer h-3 w-64 max-w-full rounded" />
                    <div class="skeleton-shimmer h-3 w-28 max-w-full rounded" />
                  </div>
                  {/* `min-w-0`: senza, la colonna destra ha come minimo il suo
                      figlio più largo (96px) e spinge fuori dalla card. */}
                  <div class="flex min-w-0 flex-col items-end gap-2">
                    <div class="skeleton-shimmer h-4 w-20 max-w-full rounded" />
                    <div class="skeleton-shimmer h-6 w-24 max-w-full rounded-full" />
                  </div>
                </div>
                <div class="mt-5 flex gap-2 border-t border-border pt-4">
                  <div class="skeleton-shimmer h-6 w-16 max-w-full rounded-full" />
                  <div class="skeleton-shimmer h-6 w-20 max-w-full rounded-full" />
                  <div class="skeleton-shimmer h-6 w-14 max-w-full rounded-full" />
                </div>
              </div>
            )}
          </Index>
        </div>
      </div>
    </Show>
  );
};
